import {
	ChangeEvent,
	Fragment,
	useEffect,
	useRef,
	useCallback,
	useContext,
	RefObject
} from 'react';
import {
	H3,
	H4,
	Spacer,
	Text,
	Tile,
	InfoChip,
	Caption,
	Grid,
	AddressPicker,
	Timepicker,
	InputField
} from '@components';
import { EditShipment, EditShipmentCargos } from '@state';
import { useRecoilState, useRecoilValue } from 'recoil';
import styles from '../EditShipment.module.scss';
import ArrowUpIcon from '@mui/icons-material/NorthEast';
import ArrowDownIcon from '@mui/icons-material/SouthEast';
import {
	CHIP_SIZE,
	TypeAheadOption,
	PartialInputEvent,
	TCargoEOS,
	TPagedDialogContext
} from '@types';
import { EditShipmentContext } from '../EditShipment.Context';
import { groupCargosByStop } from '@utils';

type TProps = {
	pagesRef?: RefObject<HTMLElement>;
	context?: TPagedDialogContext;
}

export const Location = (props: TProps) => {
	const { context, pagesRef } = props;
	const ctx = useContext(EditShipmentContext);
	const locationAccess = ctx?.access.pages.location;
	const isScrollRef = useRef(-1);
	const subpages = useRef<(HTMLDivElement | null)[]>([]);
	const [shipment, setShipment] = useRecoilState(EditShipment);
	const cargos = useRecoilValue(EditShipmentCargos);
	const addressDisabled = locationAccess?.fields.address.disabled ?? true;
	const timeDisabled = locationAccess?.fields.pickupTimes.disabled ?? true;

	// Cargos collected from the same place are one stop, so an edit applies to every cargo on it.
	const { pickups: pickupGroups, dropoffs: dropoffGroups } = groupCargosByStop(cargos ?? []);

	const updater = (name: string, value: string | Date | number, dest: 'pickup' | 'dropoff', index: number) => {
		if (shipment) {
			const update = {
				...shipment,
				cargos: shipment?.cargos.map((cargo, i) => {
					if (i === index) {
						return {
							...cargo,
							[dest]: {
								...cargo[dest],
								[name]: value
							}
						}
					}
					return cargo;
				})
			}
			setShipment(update);
		}
	}

	const selectAddress = (v: TypeAheadOption, dest: 'pickup' | 'dropoff', index: number) => {
		const value = typeof v.value === 'string' ? v.value : v.value.geo_address.street_address;
		updater('address', value, dest, index);
	}

	const updateField = (e: ChangeEvent<HTMLInputElement> | PartialInputEvent, dest: 'pickup' | 'dropoff', index: number) => {
		const { name, value } = e.target;
		updater(name, value, dest, index);
	}

	const generateStopTile = useCallback((cargo: TCargoEOS, dest: 'pickup' | 'dropoff', stopIndex: number, index: number) => {
		// The stop's own ordinal, falling back to render order for shipments booked before it was stored.
		const stopNumber = (dest === 'pickup' ? cargo.pick_position : cargo.drop_position) ?? stopIndex;
		return (
			<div ref={(el) => (subpages.current[stopIndex] = el)}>
				<Tile stretch>
					<div className={styles.row} style={{ justifyContent: 'space-between' }}>
						<Caption>Stop {stopNumber + 1}</Caption>
						<InfoChip text={dest === 'pickup' ? 'Pick-up' : 'Drop-off'} CustomIcon={dest === 'pickup' ? ArrowUpIcon : ArrowDownIcon} size={CHIP_SIZE.SMALL} />
					</div>
					<H4>{cargo[dest === 'pickup' ? 'pickup' : 'dropoff'].business_name}</H4>
					<Text>{cargo[dest === 'pickup' ? 'pickup' : 'dropoff'].address}</Text>
					<Spacer size={20} />
					<Grid columns={4} minWidth={'100px'}>
						<Grid.Item columnSpan={4}>
							<AddressPicker
								label={'Street Address'}
								name={'address'}
								value={cargo[dest].address}
								onChange={(e) => updateField(e, dest, index)}
								locationType={'COMPLETE_ADDRESS'}
								onSelected={(v) => selectAddress(v as TypeAheadOption, dest, index)}
								options={null}
								placeholder={''}
								overrideUrl={`/api/address-api/autocomplete?term={1}&locality_type={2}`}
								showArrowIcon
								fetchSavedAddresses
								required
								keepErrorSpace={false}
								disabled={addressDisabled}
							/>
						</Grid.Item>
						<Grid.Item columnSpan={2}>
							<InputField
								name={''}
								label={'Suburb'}
								required
								disabled={addressDisabled}
								value={cargo[dest].suburb}
								onChange={() => {}}
								keepErrorSpace={false}
							/>
						</Grid.Item>
						<InputField
							name={''}
							label={'State'}
							required
							disabled={addressDisabled}
							value={cargo[dest].state}
							onChange={() => {}}
							keepErrorSpace={false}
						/>
						<InputField
							name={''}
							label={'Postcode'}
							required
							disabled={addressDisabled}
							value={cargo[dest].subpost}
							onChange={() => {}}
							keepErrorSpace={false}
						/>
						<Timepicker
							label={'Opening hour'}
							name={'open_time'}
							value={cargo[dest].open_time ?? ''}
							onChange={(e) => updateField(e as PartialInputEvent, dest, index)}
							required
							disabled={timeDisabled}
						/>
						<Timepicker
							label={'Closing hour'}
							name={'close_time'}
							value={cargo[dest].close_time ?? ''}
							onChange={(e) => updateField(e as PartialInputEvent, dest, index)}
							required
							disabled={timeDisabled}
						/>
					</Grid>
				</Tile>
			</div>
		)
	}, [shipment]);

	const onPagesScroll = () => {
		if (context && pagesRef && pagesRef.current && subpages) {
			const curPos = pagesRef.current.scrollTop;
			isScrollRef.current = curPos;
			setTimeout(() => {
				if (curPos !== isScrollRef.current) {
					isScrollRef.current = -1;
				}
			}, 100);
			subpages.current.forEach((sp, i) => {
				if (sp) {
					const threshold = 200;
					if (curPos < sp.offsetTop && curPos > sp.offsetTop - threshold) {
						context.setActiveSubMenu(i);
					}
				}
			});
		}
	}

	useEffect(() => {
		if (context && pagesRef && pagesRef.current && subpages) {
			const spTops = subpages.current.map((sp, i) => {
				if (i === 0) return 0;
				if (sp) return sp.offsetTop ?? 0;
				return 0;
			});
			if (isScrollRef.current < 0) {
				pagesRef.current?.scrollTo({
					top: context.activeSubMenu === 0 ? spTops[0] : spTops[context.activeSubMenu],
					behavior: 'smooth'
				});
			}
		}
		if (pagesRef && pagesRef.current) {
			pagesRef.current.addEventListener('scroll', onPagesScroll);
		}
		return () => {
			pagesRef && pagesRef.current && pagesRef.current.removeEventListener('scroll', onPagesScroll);
		}
	}, [context, pagesRef]);

	return (
		<>
			<H3>Location Details</H3>
			<Spacer size={16} />
			<Text>Update arrival times and access details for both pick-up and drop-off locations to ensure smooth scheduling.</Text>
			<Spacer size={16} />
			{cargos && pickupGroups.map((group, groupIndex) => {
				return <Fragment key={groupIndex}>
					{generateStopTile(cargos[group[0]], 'pickup', groupIndex, group[0])}
					<Spacer size={16} />
				</Fragment>
			})}
			{/* The drop-off panels take the slots after the pick-up panels, matching the menu order. */}
			{cargos && dropoffGroups.map((group, i) => (
				<Fragment key={`dropoff-${i}`}>
					{generateStopTile(cargos[group[0]], 'dropoff', pickupGroups.length + i, group[0])}
				</Fragment>
			))}
		</>
	);
}
